/**
 * Moonwalk tuning + the pure cooldown predicate (Moonwalk, ADR-0145).
 *
 * A moonwalk glides the player backwards (opposite their facing) up to
 * {@link MOONWALK_TILES} tiles while the walk clip plays and the facing stays put -
 * the mirror of {@link ../sim/mobile.intentIsTurnOnly}, which turns without stepping
 * (moonwalk steps without turning). The **movement** is real sim state driven by
 * {@link ../sim/mobile.Mobile.startMoonwalk}; this module holds the small, pure pieces
 * that both the sim and the authority (and the client mirror) share.
 *
 * Unlike the dice / emote / door cooldowns - which live in `server/` because only the
 * server ever needs them - the moonwalk cooldown lives here in `sim/` on purpose: the
 * local player **predicts** the glide (all local movement is prediction-driven), so the
 * client must apply the *same* cooldown as the authority to avoid predicting a glide the
 * server will reject and then rubber-banding. A single shared source keeps the two in
 * lock-step, and the client never imports `server/`. It stays sim-pure: a plain constant
 * and a predicate that takes `now` as an argument, so it reads no wall clock (the
 * `architecture.test.ts` rule), exactly like the pure {@link ../sim/cosmeticRoll} engine.
 */

/**
 * How many tiles a single moonwalk glides backward. Fewer when a wall / water / the map
 * edge blocks the tile behind (the glide stops early on the last reachable tile).
 */
export const MOONWALK_TILES = 5;

/**
 * Per-tile slow-down factor for the glide: a moonwalk step takes this multiple of a
 * normal walk step (so it reads as a deliberate backward slide, not a walk). 1.75x turns
 * the default 250ms walk into a ~440ms glide step - clearly slower, still fluid.
 */
export const MOONWALK_SLOWDOWN = 1.75;

/**
 * The per-player moonwalk cooldown: a player may start at most one moonwalk per this
 * window. A glide crosses up to five tiles and takes ~2s, so this is far longer than a
 * tap emote's 1s - long enough that spamming the hotkey (or `[moonwalk`) can never chain
 * glides. Server-enforced against the session's last-moonwalk stamp; the client mirrors
 * it against its own clock so it never predicts a glide the authority will drop.
 */
export const MOONWALK_COOLDOWN_MS = 10_000;

/**
 * Whether a player whose last moonwalk began at `lastMoonwalkAt` (ms; `undefined` if they
 * have never moonwalked) may start another at wall-clock `now`. Inclusive of the far edge
 * (allowed once the full {@link MOONWALK_COOLDOWN_MS} has elapsed); a first-ever moonwalk
 * is always allowed. Mirrors {@link ../../server/emote.canEmote} but lives in `sim/` so
 * both the authority and the predicting client can share it.
 */
export function canMoonwalk(
  lastMoonwalkAt: number | undefined,
  now: number,
  cooldownMs: number = MOONWALK_COOLDOWN_MS,
): boolean {
  return lastMoonwalkAt === undefined || now - lastMoonwalkAt >= cooldownMs;
}
